import { jsx } from "react/jsx-runtime";
import {
  getNumberMaskedValue,
  useNumberMask
} from "@elliemae/ds-form-helpers-mask-hooks";
import _ from "lodash";
import { useEffect, useState, useRef } from "react";
import { DSInputText } from "@elliemae/ds-form-input-text";
import { DSFormLayoutBlockItem } from "@elliemae/ds-form-layout-blocks";
import { Grid } from "@elliemae/ds-grid";
import { useFormState, useWatch } from "react-hook-form";
import { useTextboxFieldUpdateLogic } from "../utils/fieldUpdateLogic";
import { useFieldVisibilityAndDisability } from "../utils/fieldVisibilityAndDisability";
import {
  ltvValue,
  cltvValue,
  hcltvValue,
  loanAmountValue,
  calculateLoanAmountMaxLine
} from "../utils/values";
import { useLookupOptions } from "../utils/utils";
import { isHELOC, isGovie, isIRRRL, isVA } from "../utils/helpers";
const Ltv = ({
  renderedField,
  schemaForm,
  rules,
  id,
  name,
  label,
  required,
  pathValue,
  feedbackMessage,
  type,
  ui,
  visible,
  disabled,
  disableCheck,
  visibleCheck,
  preview
}) => {
  const PERCENT_VALUES = {
    allowNegative: true,
    decimalPlaces: 3,
    suffix: "%",
    includeThousandsSeparator: false
  };
  const textboxUpdateLogic = useTextboxFieldUpdateLogic();
  const loanTerms = useLookupOptions("loanTerms");
  const ltvRef = useRef(false);
  const { isVisible, isDisabled } = useFieldVisibilityAndDisability({
    schemaForm,
    rules,
    visible,
    disabled,
    visibleCheck,
    disableCheck,
    preview
  });
  const { errors } = useFormState({ control: schemaForm.control });
  const errorMessage = _.get(errors, pathValue)?.message;
  const stringValue = String(renderedField?.value ?? "");
  const [value, setValue] = useState(
    getNumberMaskedValue(stringValue, PERCENT_VALUES)
  );
  useEffect(() => {
    setValue(getNumberMaskedValue(stringValue, PERCENT_VALUES));
  }, [renderedField?.value]);
  const inputProps = useNumberMask({
    valueSetter: setValue,
    ...PERCENT_VALUES
  });
  const [
    financedAmount,
    feeAmountFinanced,
    firstMortgageAmount,
    loanPurpose,
    lienPosition,
    loanAmountMaxLine,
    secondMortgageAmount,
    totalMortgageAmount,
    propertyValue,
    specialProducts,
    standardProducts,
    productType,
    totSubClosedEndMort,
    otherHELOCDraws,
    otherHELOCLines
  ] = useWatch({
    control: schemaForm.control,
    name: [
      "loanInformation.financedAmount",
      "loanInformation.feeAmountFinanced",
      "loanInformation.firstMortgageAmount",
      "loanInformation.loanPurpose",
      "loanInformation.lienPosition",
      "loanInformation.loanAmountMaxLine",
      "loanInformation.secondMortgageAmount",
      "loanInformation.totalMortgageAmount",
      "property.value",
      "specialProducts",
      "standardProducts",
      "productType",
      "loanInformation.totSubClosedEndMort",
      "loanInformation.otherHELOCDraws",
      "loanInformation.otherHELOCLines"
    ]
  });
  useEffect(() => {
    const customDependencies = {
      firstMortgageAmount,
      financedAmount,
      lienPosition,
      loanAmountMaxLine,
      loanPurpose,
      secondMortgageAmount,
      totalMortgageAmount,
      propertyValue,
      specialProducts,
      standardProducts,
      productType,
      otherHELOCDraws,
      otherHELOCLines,
      totSubClosedEndMort,
      isHELOC: isHELOC(productType, loanTerms),
      isGovie: isGovie(standardProducts),
      isIRRRL: isIRRRL(specialProducts),
      isVA: isVA(standardProducts)
    };
    if (name === "LTV") {
      const current = schemaForm.getValues("loanInformation.ltv");
      const calculated = ltvValue({ customDependencies });
      if (!ltvRef.current && (current !== calculated || current === void 0)) {
        schemaForm.setValue("loanInformation.ltv", calculated);
        setValue(calculated);
      }
    }
    if (name === "CLTV") {
      schemaForm.setValue(
        "loanInformation.cltv",
        cltvValue({ customDependencies })
      );
    }
    if (name === "HCLTV") {
      schemaForm.setValue(
        "loanInformation.hcltv",
        hcltvValue({ customDependencies })
      );
    }
  }, [
    firstMortgageAmount,
    financedAmount,
    lienPosition,
    loanAmountMaxLine,
    loanPurpose,
    secondMortgageAmount,
    totalMortgageAmount,
    propertyValue,
    specialProducts,
    standardProducts,
    productType,
    otherHELOCDraws,
    otherHELOCLines,
    totSubClosedEndMort
  ]);
  useEffect(() => {
    ltvRef.current = false;
  }, [
    firstMortgageAmount,
    secondMortgageAmount,
    loanAmountMaxLine,
    financedAmount,
    feeAmountFinanced
  ]);
  return /* @__PURE__ */ jsx(Grid, { span: ui.col_size, style: { display: isVisible ? "block" : "none" }, children: /* @__PURE__ */ jsx(
    DSFormLayoutBlockItem,
    {
      name,
      label,
      required,
      feedbackMessage,
      hasError: !!errors[id] || !!errorMessage,
      validationMessage: errors[id]?.message?.toString() || errorMessage,
      children: /* @__PURE__ */ jsx(
        DSInputText,
        {
          ...renderedField,
          "data-testid": "textbox",
          value,
          ...inputProps,
          onChange: (e) => {
            inputProps.onChange(e);
            const parsedValue = e.target.value.replace(/[^0-9.-]/g, "");
            const numericValue = parsedValue === "" ? null : parseFloat(parsedValue);
            textboxUpdateLogic(renderedField, schemaForm, id, {
              loanTerms
            })({
              ...e,
              target: { ...e.target, value: numericValue }
            });
          },
          onBlur: () => {
            if (name === "LTV") {
              const customDependencies = {
                firstMortgageAmount,
                lienPosition,
                loanAmountMaxLine,
                loanPurpose,
                secondMortgageAmount,
                specialProducts,
                standardProducts,
                totalMortgageAmount,
                propertyValue,
                productType
              };
              const getLoanValue = loanAmountValue(
                schemaForm.getValues("loanInformation.ltv"),
                { customDependencies }
              );
              const getLoanMaxValue = calculateLoanAmountMaxLine(
                schemaForm.getValues("loanInformation.ltv"),
                propertyValue
              );
              ltvRef.current = true;
              if (isHELOC(productType, loanTerms)) {
                schemaForm.setValue(
                  "loanInformation.loanAmountMaxLine",
                  getLoanMaxValue
                );
              } else {
                schemaForm.setValue(
                  "loanInformation.firstMortgageAmount",
                  getLoanValue
                );
              }
            }
          },
          disabled: isDisabled,
          id: pathValue,
          placeholder: "0%"
        }
      )
    }
  ) });
};
export {
  Ltv
};
